import React, { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  Building,
  PenTool,
  Image as ImageIcon,
  Layers,
  Users,
  BookOpen,
  CalendarCheck,
  Mail,
  Menu,
  X,
  LogOut,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const navItems = [
  { label: 'Overview', icon: LayoutGrid, to: '/dashboard' },
  {
    label: 'Properties',
    icon: Building,
    children: [
      { label: 'Listings', to: '/dashboard/properties' },
      { label: 'Apartments', to: '/dashboard/apartments' },
      { label: 'Categories', to: '/dashboard/property-categories' },
    ],
  },
  {
    label: 'Blog Posts',
    icon: BookOpen,
    children: [
      { label: 'Posts', to: '/dashboard/blogs' },
      { label: 'Categories', to: '/dashboard/blog-categories' },
    ],
  },
  { label: 'Hero Slides', icon: ImageIcon, to: '/dashboard/hero-slides' },
  {
    label: 'Gallery',
    icon: ImageIcon,
    children: [
      { label: 'Images', to: '/dashboard/gallery' },
      { label: 'Categories', to: '/dashboard/gallery-categories' },
    ],
  },
  { label: 'Bookings', icon: CalendarCheck, to: '/dashboard/bookings' },
  { label: 'Contact Messages', icon: Mail, to: '/dashboard/contact-messages' },
  { label: 'Testimonials', icon: Users, to: '/dashboard/testimonials' },
  { label: 'User Management', icon: Users, to: '/dashboard/users' },
  {
    label: 'Team',
    icon: Users,
    children: [
      { label: 'Members', to: '/dashboard/team' },
      { label: 'Board Members', to: '/dashboard/board-members' },
      { label: 'Roles', to: '/dashboard/team-roles' },
      { label: 'Listing Team', to: '/dashboard/listing-team' },
    ],
  },
];

const linkClasses =
  'flex items-center gap-3 rounded-xl px-4 py-2 text-sm font-medium transition hover:bg-white/10';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const renderNavLinks = (onNavigate, showLabels = !collapsed) =>
    navItems.map((item) => {
      const Icon = item.icon;
      if (item.children) {
        const isExpanded = expandedMenus[item.label];
        return (
          <div key={item.label}>
            <button
              onClick={() => setExpandedMenus(prev => ({ ...prev, [item.label]: !prev[item.label] }))}
              className={`${linkClasses} w-full justify-start text-white/80 hover:bg-white/10`}
            >
              <Icon className="h-4 w-4" />
              {showLabels && (
                <>
                  <span className="flex-1 text-left">{item.label}</span>
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </>
              )}
            </button>
            {isExpanded && showLabels && (
              <div className="ml-4 space-y-1">
                {item.children.map((child) => (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    className={({ isActive }) =>
                      `${linkClasses} ${isActive ? 'bg-white/15 text-lime-300' : 'text-white/70'}`
                    }
                    onClick={onNavigate}
                  >
                    {child.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      }
      return (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/dashboard'}
          className={({ isActive }) =>
            `${linkClasses} ${isActive ? 'bg-white/15 text-lime-300' : 'text-white/80'}`
          }
          onClick={onNavigate}
        >
          <Icon className="h-4 w-4" />
          {showLabels && item.label}
        </NavLink>
      );
    });

  return (
    <div className="flex min-h-screen flex-col bg-stone-50 lg:flex-row">
      {/* Desktop Sidebar */}
      <aside
        className={`sticky top-0 hidden h-screen bg-[#140b07] text-white transition-all duration-200 lg:flex lg:flex-col ${
          collapsed ? 'lg:w-20' : 'lg:w-64'
        }`}
      >
        <div className="flex h-16 items-center justify-between px-4">
          <Link to="/" className="text-lg font-semibold tracking-wide text-lime-400">
            {collapsed ? 'N.' : 'Nyinimu CMS'}
          </Link>
          <button
            onClick={() => setCollapsed((prev) => !prev)}
            className="rounded-lg border border-white/20 px-2 py-1 text-xs uppercase tracking-widest text-white/70"
          >
            {collapsed ? '>' : '<'}
          </button>
        </div>
        <nav className="mt-6 space-y-1 px-3">{renderNavLinks(undefined, !collapsed)}</nav>
        <div className="mt-auto px-4 pb-6">
          <button
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 px-4 py-2 text-sm font-medium text-white/90 hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" />
            {!collapsed && 'Logout'}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="flex-1 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <div className="relative w-72 bg-[#140b07] p-6 text-white shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <Link to="/" className="text-lg font-semibold tracking-wide text-lime-400">
                Nyinimu CMS
              </Link>
              <button
                onClick={() => setMobileNavOpen(false)}
                className="rounded-full border border-white/20 p-2 text-white/70 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="space-y-1">{renderNavLinks(() => setMobileNavOpen(false), true)}</nav>
            <button
              onClick={handleLogout}
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 px-4 py-2 text-sm font-medium text-white/90 hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      )}

      <main className="flex-1">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-stone-200 bg-white px-4 py-4 shadow-sm sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileNavOpen(true)}
              className="rounded-lg border border-stone-200 p-2 text-stone-600 hover:bg-stone-50 lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <p className="text-sm uppercase tracking-[0.3em] text-stone-400">Nyinimu Admin</p>
            <h1 className="text-xl font-semibold text-stone-900 sm:text-2xl">Operations Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="rounded-lg border border-[#81be00] bg-[#81be00] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-[#6a9e00] hover:border-[#6a9e00]"
            >
              View Website
            </a>
            <div className="text-right">
              <p className="text-sm font-medium text-stone-700">{user?.email}</p>
              <p className="text-xs uppercase tracking-wider text-stone-400">
                {user?.user_metadata?.role || user?.app_metadata?.role || 'Admin'}
              </p>
            </div>
          </div>
        </header>
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
